// react
import { useEffect, useRef, useState } from 'react';

// material ui icons
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// material ui
import { Box, IconButton, TextField, Typography } from '@mui/material';

// api
import { useSetDisplayNameForMeterMeterDisplayNameAnalysisTypeAccountIdMeterIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
import { WritableOnly } from '@/components/access-control';

// components
// feature types
import type { AnalysisType } from '@/features/energyxplorer/types';

interface MeterDisplayNameProps {
  recalcAccountId: string | number;
  meterId: string | number;
  analysisType: AnalysisType;
  displayName?: string | null;
  address?: string | null;
}

const MeterDisplayName = ({
  recalcAccountId,
  meterId,
  analysisType,
  displayName: initialDisplayName,
  address,
}: MeterDisplayNameProps) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialDisplayName || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const [putMeterDisplayName, { isLoading }] =
    useSetDisplayNameForMeterMeterDisplayNameAnalysisTypeAccountIdMeterIdPutMutation();

  if (!editing && value !== (initialDisplayName || '')) {
    setValue(initialDisplayName || '');
  }

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }
  }, [editing]);

  const submit = async () => {
    if (!recalcAccountId || !meterId || value === initialDisplayName) {
      setEditing(false);
      return;
    }

    try {
      await putMeterDisplayName({
        accountId: recalcAccountId as number,
        meterId: meterId as number,
        analysisType,
        displayName: value,
      }).unwrap();

      setEditing(false);
    } catch (err) {
      console.error('Failed to update meter display name', err);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submit();
    } else if (e.key === 'Escape') {
      setValue(initialDisplayName || '');
      setEditing(false);
    }
  };

  return (
    <Box width="100%" sx={{ display: 'flex', alignItems: 'center' }}>
      {!editing ? (
        <WritableOnly
          fallback={
            <Typography
              variant="h6"
              data-testid="meter-display-name-title"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {value || address}
            </Typography>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h6"
              data-testid="meter-display-name-title"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {value || address}
            </Typography>
          </Box>
        </WritableOnly>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <TextField
            sx={{
              minWidth: 120,
              width: `${Math.max((value || '').length + 5, 10)}ch`,
            }}
            inputRef={inputRef}
            size="small"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isLoading}
            data-testid="meter-display-name-input"
          />
          <IconButton
            aria-label="save"
            onClick={submit}
            disabled={isLoading}
            size="small"
            sx={{ ml: 1 }}
            data-testid="meter-display-name-save"
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="cancel"
            onClick={() => {
              setValue(initialDisplayName || '');
              setEditing(false);
            }}
            disabled={isLoading}
            size="small"
            sx={{ ml: 0.5 }}
            data-testid="meter-display-name-cancel"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default MeterDisplayName;
